// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import ValidationInput from 'Ui/components/common/form/validation-input';
import SwitchInput from 'Ui/components/common/form/switch-input';
import {AssistanceTooltip, MultiSelect, Stack, IconButton, InputAdornment, Button} from '@deere/isg.component-library';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import {PPE_ICONS} from 'Ui/constants/labor-constants';
import {
    IconAdd,
    IconChaps,
    IconEyeProtection,
    IconGloves,
    IconHardHat,
    IconHearingProtection,
    IconProtectiveFootwear,
    IconRespirator,
    IconSafetySuit,
    IconSunHat,
    IconSunscreen,
    IconVest,
    IconStar,
    IconStarEmpty,
    IconInfoAlert
} from '@deere/icons';
import ValidationMultiSelect from 'Ui/components/common/form/validation-multi-select';
import PropTypes from 'Utils/prop-type-utils';
import {capitalizeFirstLetter, replaceTranslationNames} from 'Utils/translation-utils';
import {connect} from 'react-redux';
import dialogTypes from 'Ui/components/common/dialog-types';
import {openDialog} from 'Store/actions/dialogs';
import {MOWING_PATTERNS} from 'OnLabor/workboard/constants/mowing-patterns';

const PPE_ICON_STYLE = {
    style: {
        height: '20px',
        width: '20px'
    }
};

const STAR_ICON_STYLE = {
    style: {
        height: '24px',
        width: '24px'
    }
};

const NOTATION_EXCEPTION = ['e', 'E'];

const ppeIconDropdownOptions = [
    {
        title: 'Chaps',
        id: PPE_ICONS.CHAPS,
        icon: <IconChaps {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Eye Protection',
        id: PPE_ICONS.EYE_PROTECTION,
        icon: <IconEyeProtection {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Gloves',
        id: PPE_ICONS.GLOVES,
        icon: <IconGloves {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Hard Hat',
        id: PPE_ICONS.HARD_HAT,
        icon: <IconHardHat {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Hearing Protection',
        id: PPE_ICONS.HEARING_PROTECTION,
        icon: <IconHearingProtection {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Protective Footwear',
        id: PPE_ICONS.PROTECTIVE_FOOTWEAR,
        icon: <IconProtectiveFootwear {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Respirator',
        id: PPE_ICONS.RESPIRATOR,
        icon: <IconRespirator {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Safety Suit',
        id: PPE_ICONS.SAFETY_SUIT,
        icon: <IconSafetySuit {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Vest',
        id: PPE_ICONS.VEST,
        icon: <IconVest {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Sun Hat',
        id: PPE_ICONS.SUN_HAT,
        icon: <IconSunHat {...PPE_ICON_STYLE}/>
    },
    {
        title: 'Sunscreen',
        id: PPE_ICONS.SUNSCREEN,
        icon: <IconSunscreen {...PPE_ICON_STYLE}/>
    }
];
const SHARED_MULTISELECT_PROPS = {
    className: 'multiselect'
};

function EditableJobSettings(props) {
    const {
        allData,
        loading,
        setValid,
        setValues,
        updateData,
        translations,
        values,
        useEquipmentManager,
        openJobCategoryDialog
    } = props;

    const favoriteIcon = values.isFavorite ?
        (<IconStar
            primary='#367C2B'
            {...STAR_ICON_STYLE}
        />) :
        (<IconStarEmpty
            primary='#333'
            {...STAR_ICON_STYLE}
        />);

    const mowableAreas = React.useMemo(() => {
        return MOWING_PATTERNS.map((mowingPattern) => ({
            id: mowingPattern.equipmentAreaId,
            title: mowingPattern.area
        }));
    }, []);

    function onChange(event) {
        const {
            name, value
        } = event.target;

        setValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    }

    function onNonEventChange(name, value) {
        onChange({
            target: {
                name,
                value
            }
        });
    }

    React.useEffect(() => {
        if (!values.requiresCutDirections || !values.equipmentAreaId) {
            return;
        }
        if (!MOWING_PATTERNS.find((mp) => mp.equipmentAreaId === values.equipmentAreaId)) {
            onNonEventChange('equipmentAreaId', undefined);
        }
    }, [values.requiresCutDirections]);

    const categoryFooterAction = (
        <Button
            onClick={() => openJobCategoryDialog({
                jobCategory: null,
                membershipJobCategories: allData.jobCategories,
                updateJobCategories: async (jobCategoryTitle) => {
                    await updateData();
                    onNonEventChange('jobCategory', jobCategoryTitle);
                }
            })}
            startIcon={<IconAdd/>}
            title={replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': translations.PRODUCT_TYPE
            })}
            variant='borderless'
        >
            {replaceTranslationNames(translations.ONLINK_ADD_ITEM, {
                '0': translations.PRODUCT_TYPE
            })}
        </Button>
    );

    const typesMultiselectModel = useEquipmentManager ? {
        groups: allData.equipmentTypes,
        items: [],
        groupOptions: {
            collapseAll: true
        },
        label: translations.MACHINE_INSIGHTS_MACHINE_MODELS,
        labels: {
            multiSelect: translations.MACHINE_INSIGHTS_MACHINE_MODELS,
            placeholder: translations.ONLINK_SELECT_EQUIPMENT_MODELS,
            emptyList: translations.NO_ITEMS,
            allSelect: translations.ONLINK_ALL_EQUIPMENT_MODELS,
            selectAllItems: replaceTranslationNames(translations.ONLINK_SELECT_ALL, {
                '0': translations.MACHINE_INSIGHTS_MACHINE_MODELS
            }),
            noItemMatch: translations.NO_RESULTS_FOUNDS
        }
    } : {
        items: allData.equipmentTypes,
        label: translations.MACHINE_INSIGHTS_MACHINE_TYPES,
        labels: {
            multiSelect: translations.MACHINE_INSIGHTS_MACHINE_TYPES,
            placeholder: translations.ONLINK_SELECT_EQUIPMENT_TYPES,
            emptyList: translations.NO_ITEMS,
            allSelect: translations.ONLINK_ALL_EQUIPMENT_TYPES,
            selectAllItems: translations.SELECT_ALL,
            noItemMatch: translations.NO_RESULTS_FOUNDS
        }
    };

    return (
        <form className='job-dialog-form'>
            <LoadingWrapper
                className='table-loading-icon'
                loading={loading}
                size='50px'
            >
                <div className='settings-group mobile'>
                    <ValidationInput
                        autoFocus={true}
                        debounceTimeout={window.props.debounceTimeout}
                        fullWidth
                        label={translations.NAME}
                        name='title'
                        onChange={onChange}
                        placeholder={translations.ENTER_NAME}
                        required={true}
                        setValid={setValid}
                        tabIndex={0}
                        type='text'
                        value={values.title}
                    />
                    <ValidationInput
                        debounceTimeout={window.props.debounceTimeout}
                        fullWidth
                        label={translations.ONLINK_SECONDARY_NAME}
                        name='secondaryName'
                        onChange={onChange}
                        placeholder={translations.ENTER_NAME}
                        setValid={setValid}
                        tabIndex={0}
                        type='text'
                        value={values.secondaryName}
                    />
                </div>
                <div className='settings-group mobile'>
                    <ValidationMultiSelect
                        {...SHARED_MULTISELECT_PROPS}
                        footerAction={categoryFooterAction}
                        items={allData.jobCategories}
                        label={translations.PRODUCT_TYPE}
                        labels={{
                            placeholder: translations.PRODUCTS_SELECT_CATEGORY,
                            emptyList: translations.NO_ITEMS,
                            noItemMatch: translations.NO_RESULTS_FOUNDS
                        }}
                        name='jobCategory'
                        onChange={(selectedId) => onNonEventChange('jobCategory', selectedId[0])}
                        selectedIds={[values.jobCategory].filter(Boolean)}
                        setValid={setValid}
                        single
                    />
                    <ValidationMultiSelect
                        {...SHARED_MULTISELECT_PROPS}
                        error={true}
                        helperText={values.requiresCutDirections && !values.equipmentAreaId ? translations.ONLINK_JOBS_AREA_REQUIRED : ''}
                        invalidCustomError={values.requiresCutDirections && !values.equipmentAreaId}
                        items={values.requiresCutDirections ? mowableAreas : allData.equipmentAreas}
                        label={translations.AREA}
                        labels={{
                            placeholder: replaceTranslationNames(translations.SELECT_PLACEHOLDER, {
                                '0': translations.AREA
                            }),
                            emptyList: translations.NO_ITEMS,
                            noItemMatch: translations.NO_RESULTS_FOUNDS
                        }}
                        name='equipmentAreaId'
                        onChange={(selectedId) => onNonEventChange('equipmentAreaId', selectedId[0])}
                        required={values.requiresCutDirections}
                        selectedIds={[values.equipmentAreaId].filter(Boolean)}
                        setValid={setValid}
                        single
                    />
                </div>
                <div className='settings-group mobile'>
                    <ValidationInput
                        errors={{
                            rangeOverflow: `${translations.ONLINK_VALUE_LESS_THAN_OR_EQUAL} 24.`,
                            rangeUnderflow: `${translations.ONLINK_VALUE_GREATER_THAN_OR_EQUAL} 0.`
                        }}
                        fullWidth
                        label={translations.ONLINK_ESTIMATED_HOURS}
                        max={24}
                        min={0}
                        name='estDuration'
                        onChange={onChange}
                        placeholder={capitalizeFirstLetter(translations.ENTER_ESTIMATED_HOURS)}
                        setValid={setValid}
                        slotProps={{
                            input: {
                                endAdornment: <InputAdornment position='end'>{translations.HOUR_UOM}</InputAdornment>
                            }
                        }}
                        step='any'
                        tabIndex={0}
                        type='number'
                        value={values.estDuration}
                    />
                    <ValidationInput
                        errors={{
                            rangeUnderflow: `${translations.ONLINK_VALUE_GREATER_THAN_OR_EQUAL} 0.`
                        }}
                        fullWidth
                        label={
                            <Stack
                                alignItems='center'
                                direction='row'
                                spacing={1}
                            >
                                <p>{translations.ONLINK_OPERATOR_COUNT}</p>
                                <AssistanceTooltip
                                    body={translations.ONLINK_OPERATOR_COUNT_TOOLTIP}
                                    title={translations.ONLINK_OPERATOR_COUNT}
                                >
                                    <IconInfoAlert size='md'/>
                                </AssistanceTooltip>
                            </Stack>
                        }
                        min={0}
                        name='operatorCount'
                        onChange={onChange}
                        onKeyDown={(e) => NOTATION_EXCEPTION.includes(e.key) && e.preventDefault()}
                        placeholder={capitalizeFirstLetter(translations.ENTER_QUANTITY)}
                        setValid={setValid}
                        step={'any'}
                        tabIndex={0}
                        type='number'
                        value={values.operatorCount}
                    />
                </div>
                <div className='settings-group mobile'>
                    <MultiSelect
                        {...SHARED_MULTISELECT_PROPS}
                        {...typesMultiselectModel}
                        className='multiselect hide-all-option'
                        containerHeight={300}
                        enableVirtualization={true}
                        name='equipmentTypes'
                        onChange={(selectedIds) => onNonEventChange('equipmentTypes', selectedIds)}
                        selectedIds={values.equipmentTypes}
                    />
                    <MultiSelect
                        {...SHARED_MULTISELECT_PROPS}
                        allItemsLabel={translations.ONLINK_ALL_PPE}
                        className='multiselect hide-all-option'
                        containerHeight={300}
                        items={ppeIconDropdownOptions}
                        label={translations.ONLINK_PERSONAL_PROTECTION_EQUIPMENT}
                        labels={{
                            multiSelect: translations.ONLINK_PPE,
                            placeholder: translations.ONLINK_SELECT_PPE,
                            emptyList: translations.NO_ITEMS,
                            allSelect: translations.ONLINK_ALL_PPE,
                            selectAllItems: translations.SELECT_ALL,
                            noItemMatch: translations.NO_RESULTS_FOUNDS
                        }}
                        name='ppeList'
                        onChange={(selectedIds) => onNonEventChange('ppeList', selectedIds)}
                        selectedIds={values.ppeList}
                    />
                </div>
                <Stack
                    direction='column'
                    spacing={1}
                >
                    <SwitchInput
                        checked={values.requiresCutDirections}
                        name='requiresCutDirections'
                        onChange={onNonEventChange}
                        onLabel={translations.ONLINK_JOB_REQUIRES_CUTS_AND_DIRECTIONS}
                        translations={translations}
                    />
                    <SwitchInput
                        checked={values.inactive}
                        name='inactive'
                        onChange={onNonEventChange}
                        onLabel={translations.inactive}
                        translations={translations}
                    />
                    <Stack
                        alignItems='center'
                        direction='row'
                        spacing={1}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onNonEventChange('isFavorite', !values.isFavorite);
                            }}
                            title={translations.ONLINK_FAVORITE_JOB}
                            variant='borderless'
                        >
                            {favoriteIcon}
                        </IconButton>
                        <p>{translations.ONLINK_FAVORITE_JOB}</p>
                        <AssistanceTooltip
                            body={translations.ONLINK_FAVORITE_JOB_TOOLTIP}
                            title={translations.ONLINK_FAVORITE_JOB}
                        >
                            <IconInfoAlert/>
                        </AssistanceTooltip>
                    </Stack>
                </Stack>
            </LoadingWrapper>
        </form>
    );
}

EditableJobSettings.propTypes = {
    allData: PropTypes.object,
    loading: PropTypes.bool,
    openJobCategoryDialog: PropTypes.func,
    setValid: PropTypes.func,
    setValues: PropTypes.func,
    translations: PropTypes.translations,
    updateData: PropTypes.func,
    useEquipmentManager: PropTypes.bool,
    values: PropTypes.object
};

export function mapDispatchToProps(dispatch) {
    return {
        openJobCategoryDialog(props) {
            dispatch(openDialog(dialogTypes.JOB_CATEGORY_DIALOG, props));
        }
    };
}

export default connect(null, mapDispatchToProps)(EditableJobSettings);
